import { StatusBar, Style } from '@capacitor/status-bar';
import { Capacitor } from '@capacitor/core';

/**
 * Status Bar Configuration Service
 * Handles status bar styling and behavior on native platforms
 */

class StatusBarService {
    private isNative: boolean;

    constructor() {
        this.isNative = Capacitor.isNativePlatform();
    }

    /**
     * Initialize status bar with default settings
     * Call this in App.tsx on mount
     */
    async initialize() {
        if (!this.isNative) return;
        
        try {
            // Set default style based on platform
            if (Capacitor.getPlatform() === 'ios') {
                await StatusBar.setStyle({ style: Style.Dark });
                // iOS: keep status bar area transparent over app background.
                await StatusBar.setOverlaysWebView({ overlay: true });
            } else {
                await StatusBar.setStyle({ style: Style.Dark });
                await StatusBar.setOverlaysWebView({ overlay: false });
                // Android uses an explicit status bar background color.
                await StatusBar.setBackgroundColor({ color: '#F6F6F6' });
            }

            console.log('Status bar initialized successfully');
        } catch (error) {
            console.warn('Failed to initialize status bar:', error);
        }
    }

    /**
     * Set light status bar (dark text/icons)
     * Use for light backgrounds
     */
    async setLight() {
        if (!this.isNative) return;
        try {
            await StatusBar.setStyle({ style: Style.Light });
        } catch (error) {
            console.warn('Failed to set status bar style:', error);
        }
    }

    /**
     * Set dark status bar (light text/icons)
     * Use for dark backgrounds
     */
    async setDark() {
        if (!this.isNative) return;
        try {
            await StatusBar.setStyle({ style: Style.Dark });
        } catch (error) {
            console.warn('Failed to set status bar style:', error);
        }
    }

    /**
     * Set status bar background color
     * @param color Hex color string (e.g., '#FFFFFF')
     */
    async setBackgroundColor(color: string) {
        if (!this.isNative) return;
        try {
            await StatusBar.setBackgroundColor({ color });
        } catch (error) {
            console.warn('Failed to set status bar background color:', error);
        }
    }

    /**
     * Show status bar
     */
    async show() {
        if (!this.isNative) return;
        try {
            await StatusBar.show();
        } catch (error) {
            console.warn('Failed to show status bar:', error);
        }
    }

    /**
     * Hide status bar
     * Use sparingly - most apps should keep status bar visible
     */
    async hide() {
        if (!this.isNative) return;
        try {
            await StatusBar.hide();
        } catch (error) {
            console.warn('Failed to hide status bar:', error);
        }
    }

    /**
     * Set overlay mode
     * @param overlay If true, content goes behind status bar
     */
    async setOverlay(overlay: boolean) {
        if (!this.isNative) return;
        try {
            await StatusBar.setOverlaysWebView({ overlay });
        } catch (error) {
            console.warn('Failed to set status bar overlay:', error);
        }
    }

    /**
     * Get current status bar info
     */
    async getInfo() {
        if (!this.isNative) return null;
        try {
            return await StatusBar.getInfo();
        } catch (error) {
            console.warn('Failed to get status bar info:', error);
            return null;
        }
    }
}

// Export singleton instance
export const StatusBarConfig = new StatusBarService();

// Export class for testing
export default StatusBarService;
